import type { FC } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Show } from '@components/Show';
import { Table } from '@components/Table';
import { StatusCell } from '@ContractBuilder/contract-builder-header/components/StatusCell';
import type { EndorsementData, EntityData } from '@ContractBuilder/types';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { formatDate, sortByDate } from '@root/helpers';
import Nav from '@src/routes';
import { createColumnHelper } from '@tanstack/react-table';

import { CreateEndorsementButton } from './CreateEndorsementButton';

interface ListEndorsementsButtonProps {
  data?: EndorsementData[];
  isLoading: boolean;
  submission: EntityData;
}

const columnHelper = createColumnHelper<EndorsementData>();

const columns = [
  columnHelper.accessor('reference', {
    id: 'reference',
    header: 'Reference',
    size: 160,
    cell: (info) => {
      const value = info.getValue();

      return (
        <p className="text-info-500 truncate" title={value}>
          {value}
        </p>
      );
    },
  }),
  columnHelper.accessor('internal_description', {
    id: 'internal_description',
    header: 'Description',
    cell: (info) => {
      const value = info.getValue();
      return (
        <p className="text-info-500 break-words" title={value}>
          {value}
        </p>
      );
    },
  }),
  columnHelper.accessor('updated_at', {
    id: 'updated_at',
    header: 'Updated',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return <p className="text-info-500 whitespace-nowrap">{formatDate(value, 'DD MMM YYYY HH:mm')}</p>;
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 124,
    cell: (info) => {
      return <StatusCell status={info.getValue()} />;
    },
  }),
  columnHelper.accessor('effective_date', {
    id: 'effective_date',
    header: 'Effective',
    size: 136,
    cell: (info) => {
      const value = info.getValue();
      return <p className="text-info-500 whitespace-nowrap">{formatDate(value, 'DD MMM YYYY')}</p>;
    },
  }),
];

export const ListEndorsementsButton: FC<ListEndorsementsButtonProps> = ({ data = [], isLoading, submission }) => {
  const navigate = useNavigate();

  const getFeatureFlagState = useFeatureFlags();
  const disableRecordCreation = getFeatureFlagState('DisableRecordCreation');

  const handleItemClick = (item: EntityData) => {
    const endorsementContractPath = generatePath(Nav.EndorsementView, {
      id: submission.id,
      endorsement_id: item.id,
    });

    navigate(endorsementContractPath);
  };

  return (
    <div>
      <h2 className="text-info-900 px-4 pt-4 pb-2 text-lg leading-normal">Endorsements</h2>
      <div className="max-h-[max(10rem,calc(50vh-10rem))] overflow-y-auto shadow-inner">
        <Table<EndorsementData>
          className="table-fixed"
          data={sortByDate(data, 'desc', 'updated_at')}
          emptyRowsLabel="No endorsements available"
          columns={columns}
          isLoading={isLoading}
          onRowClick={handleItemClick}
          rowsSkeletonLength={submission.endorsementsCount}
        />
      </div>
      <Show when={!disableRecordCreation}>
        <CreateEndorsementButton
          className="align-start border-info-200 w-full items-center rounded-tl-none rounded-tr-none border-t border-r-0 border-b-0 border-l-0"
          isDisabled={isLoading}
        />
      </Show>
    </div>
  );
};
